/*
 * Cocoa Asante — Klaviyo Reviews, branded.
 *
 * Reads reviews from the same public, read-only endpoints Klaviyo's own onsite
 * widget uses (fast.a.klaviyo.com/reviews/api/client_reviews/…, keyed by the
 * public company id), and renders them in Cocoa Asante's own markup:
 *
 *   [data-ca-rv-open]     opens the reviews pop-up (snippets/ca-reviews-modal)
 *                         data-ca-rv-product = Shopify product id, or "all"
 *                         data-ca-rv-title   = pop-up heading
 *                         data-ca-rv-view    = "write" to open straight on the form
 *   [data-ca-rv-home]     homepage plaque: live store-wide rating + random cards
 *   [data-ca-rv-badge]    a star badge with no server-side rating: filled live
 *
 * Everything degrades: openers are real links (to #reviews or Klaviyo's hosted
 * form), and the homepage keeps its server-rendered rating if Klaviyo is
 * unreachable. Review text is always inserted escaped.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('ca-rv-config');
  if (!cfgEl) return;
  var cfg = {};
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var CID = cfg.companyId;
  if (!CID) return;

  var API = 'https://fast.a.klaviyo.com/reviews/api/client_reviews/';
  var WRITE = 'https://reviews-app.services.klaviyo.com/rr/';
  var PAGE = 10;
  // Klaviyo refuses offsets past ~600 and pages past 50.
  var MAX_OFFSET = 550;
  var MAX_LIMIT = 50;

  /* ---------- helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var nf;
  try { nf = new Intl.NumberFormat(cfg.locale || undefined); } catch (e) { nf = new Intl.NumberFormat(); }
  function fmtCount(n) { return nf.format(n || 0); }
  function fmtAvg(n) { return (Math.round((n || 0) * 10) / 10).toFixed(1); }
  // Exact rating for the five-star row (.ca-stars reads --r, 0-5).
  function rVal(n) { return String(Math.max(0, Math.min(5, Number(n) || 0))); }
  function plural(n, one, many) { return n === 1 ? one : many; }

  var rtf = null;
  try { rtf = new Intl.RelativeTimeFormat(cfg.locale || undefined, { numeric: 'auto' }); } catch (e) {}
  function ago(iso) {
    var t = Date.parse(iso);
    if (!t) return '';
    var s = (t - Date.now()) / 1000;
    var units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (var i = 0; i < units.length; i++) {
      if (Math.abs(s) >= units[i][1] || units[i][0] === 'minute') {
        var v = Math.round(s / units[i][1]);
        return rtf ? rtf.format(v, units[i][0]) : new Date(t).toLocaleDateString();
      }
    }
    return '';
  }

  function stars(rating, extraClass) {
    return '<span class="ca-stars' + (extraClass ? ' ' + extraClass : '') + '" style="--r: ' + rVal(rating) + '" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></span>' +
      '<span class="visually-hidden">Rated ' + fmtAvg(rating) + ' out of 5</span>';
  }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  function trimWords(text, max) {
    text = String(text || '').replace(/\s+/g, ' ').trim();
    if (text.length <= max) return text;
    var cut = text.slice(0, max);
    var sp = cut.lastIndexOf(' ');
    return (sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,;:.!?-]+$/, '') + '…';
  }

  /* ---------- API ---------- */

  var groupedCache = {};
  function groupedId(id) {
    if (id === 'all') return Promise.resolve(id);
    if (groupedCache[id]) return groupedCache[id];
    groupedCache[id] = fetch(API + encodeURIComponent(id) + '/grouped_id/?company_id=' + encodeURIComponent(CID))
      .then(function (r) { return r.json(); })
      .then(function (d) { return typeof d.grouped_id === 'string' ? d.grouped_id : id; })
      .catch(function () { return id; });
    return groupedCache[id];
  }

  function getPage(o) {
    return groupedId(String(o.productId)).then(function (gid) {
      var q = new URLSearchParams({
        product_id: gid,
        company_id: CID,
        limit: String(Math.min(o.limit || PAGE, MAX_LIMIT)),
        offset: String(o.offset || 0),
        sort: String(o.sort == null ? 3 : o.sort),
        filter: o.filter || '',
        type: 'reviews',
        media: 'false',
        kl_review_uuid: ''
      });
      if (o.rating) q.append('rating', String(o.rating));
      return fetch(API + encodeURIComponent(gid) + '/?' + q.toString())
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || !Array.isArray(d.reviews)) throw new Error((d && d.error_code) || 'klaviyo_error');
          return d;
        });
    });
  }

  function getSummaries(ids) {
    var nums = ids.map(Number).filter(Boolean);
    if (!nums.length) return Promise.resolve({});
    return fetch(API + '?company_id=' + encodeURIComponent(CID) + '&products=' + encodeURIComponent(JSON.stringify(nums)))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var map = {};
        (d.products || []).forEach(function (p) { map[String(p.external_id || p.shopify_id)] = p; });
        return map;
      });
  }

  function writeUrl(target) {
    var u = WRITE + encodeURIComponent(target || 'store') + '?company_id=' + encodeURIComponent(CID) + '&onsiteEnabled=true';
    if (cfg.locale) u += '&lang=' + encodeURIComponent(cfg.locale);
    return u;
  }

  /* ---------- pop-up ---------- */

  var dlg = document.getElementById('ca-rv');
  var M = null;

  if (dlg && typeof dlg.showModal === 'function') {
    var $ = function (sel) { return dlg.querySelector(sel); };
    M = {
      title: $('[data-ca-rv-title]'),
      summary: $('[data-ca-rv-summary]'),
      stars: $('[data-ca-rv-stars]'),
      avg: $('[data-ca-rv-avg]'),
      count: $('[data-ca-rv-count]'),
      bars: $('[data-ca-rv-bars]'),
      tabcount: $('[data-ca-rv-tabcount]'),
      list: $('[data-ca-rv-list]'),
      status: $('[data-ca-rv-status]'),
      more: $('[data-ca-rv-more]'),
      filter: $('[data-ca-rv-filter]'),
      sort: $('[data-ca-rv-sort]'),
      rating: $('[data-ca-rv-rating]'),
      listView: $('[data-ca-rv-view="list"]'),
      writeView: $('[data-ca-rv-view="write"]'),
      frame: $('[data-ca-rv-frame]'),
      frameLoading: $('[data-ca-rv-frame-loading]'),
      foot: $('[data-ca-rv-foot]'),
      state: null,
      token: 0
    };
  }

  function skeleton(n) {
    var h = '';
    for (var i = 0; i < n; i++) {
      h += '<li class="ca-rv__item ca-rv__item--skeleton" aria-hidden="true"><span style="width:90px"></span><span style="width:40%"></span><span style="width:92%"></span><span style="width:70%"></span></li>';
    }
    return h;
  }

  function reviewItem(r, showProduct) {
    var product = showProduct && r.product && r.product.name ? '<span class="ca-rv__product">' + esc(r.product.name) + '</span>' : '';
    var verified = r.verified ? '<span class="ca-rv__verified"><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Verified buyer</span>' : '';
    var reply = r.public_reply_content
      ? '<div class="ca-rv__reply"><strong>' + esc(r.public_reply_author || 'Cocoa Asante') + ' replied</strong>' + esc(r.public_reply_content) + '</div>'
      : '';
    return '<li class="ca-rv__item">' +
      '<div class="ca-rv__item-top">' + stars(r.rating) + '<time class="ca-rv__date" datetime="' + esc(r.created_at) + '">' + esc(ago(r.created_at)) + '</time></div>' +
      (r.title ? '<h3 class="ca-rv__item-title">' + esc(r.title) + '</h3>' : '') +
      '<p class="ca-rv__byline"><span class="ca-rv__author">' + esc(r.author || 'Cocoa Asante customer') + '</span>' + verified + product + '</p>' +
      (r.content ? '<p class="ca-rv__content">' + esc(r.content) + '</p>' : '') +
      reply +
      '</li>';
  }

  function renderSummary(sum) {
    if (!sum || !sum.review_count) {
      M.summary.hidden = true;
      M.bars.hidden = true;
      return;
    }
    M.summary.hidden = false;
    M.stars.style.setProperty('--r', rVal(sum.star_rating));
    M.avg.textContent = fmtAvg(sum.star_rating);
    M.count.textContent = fmtCount(sum.review_count) + ' ' + plural(sum.review_count, 'review', 'reviews');
    M.summary.setAttribute('aria-label', 'Rated ' + fmtAvg(sum.star_rating) + ' out of 5 from ' + fmtCount(sum.review_count) + ' reviews');

    var hist = sum.rating_histogram;
    if (Array.isArray(hist) && hist.length === 5) {
      var max = Math.max.apply(null, hist) || 1;
      var h = '';
      for (var s = 5; s >= 1; s--) {
        var n = hist[s - 1] || 0;
        h += '<button type="button" class="ca-rv__bar" data-ca-rv-bar="' + s + '" aria-pressed="false" aria-label="Show ' + s + '-star reviews (' + fmtCount(n) + ')">' +
          '<span>' + s + ' star' + (s > 1 ? 's' : '') + '</span>' +
          '<span class="ca-rv__bar-track"><span class="ca-rv__bar-fill" style="--w:' + (n / max * 100) + '%"></span></span>' +
          '<span class="ca-rv__bar-n">' + fmtCount(n) + '</span></button>';
      }
      M.bars.innerHTML = h;
      M.bars.hidden = false;
    }
  }

  function syncBars() {
    var cur = M.rating.value;
    M.bars.querySelectorAll('[data-ca-rv-bar]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-ca-rv-bar') === cur));
    });
  }

  function load(reset) {
    var st = M.state;
    if (!st) return;
    var token = ++M.token;
    if (reset) {
      st.offset = 0;
      M.list.innerHTML = skeleton(3);
      M.status.hidden = true;
      M.more.hidden = true;
    } else {
      M.more.setAttribute('aria-busy', 'true');
    }
    getPage({
      productId: st.productId,
      offset: st.offset,
      limit: PAGE,
      sort: M.sort.value,
      filter: M.filter.value.trim(),
      rating: M.rating.value
    }).then(function (d) {
      if (token !== M.token) return;
      if (reset) {
        M.list.innerHTML = '';
        if (!st.summaryShown) { renderSummary(d.summary); st.summaryShown = true; syncBars(); }
      }
      var html = d.reviews.map(function (r) { return reviewItem(r, st.productId === 'all'); }).join('');
      M.list.insertAdjacentHTML('beforeend', html);
      st.offset += d.reviews.length;
      var shown = d.filtered_count != null ? d.filtered_count : (d.summary && d.summary.review_count) || 0;
      M.tabcount.textContent = fmtCount(shown);
      M.more.hidden = !(d.has_more && st.offset < MAX_OFFSET + MAX_LIMIT);
      M.more.removeAttribute('aria-busy');
      if (!M.list.children.length) {
        var filtered = M.filter.value.trim() || M.rating.value;
        M.status.textContent = filtered ? 'No reviews match those filters yet.' : (cfg.empty || 'No reviews yet.');
        M.status.hidden = false;
      }
    }).catch(function () {
      if (token !== M.token) return;
      M.more.removeAttribute('aria-busy');
      if (reset) M.list.innerHTML = '';
      M.status.textContent = 'Reviews couldn’t load just now. Please try again in a moment.';
      M.status.hidden = false;
    });
  }

  function showList() {
    M.writeView.hidden = true;
    M.listView.hidden = false;
    M.foot.hidden = false;
    M.frame.removeAttribute('src');
  }

  function showWrite() {
    var st = M.state;
    M.listView.hidden = true;
    M.foot.hidden = true;
    M.writeView.hidden = false;
    M.frameLoading.hidden = false;
    M.frame.src = writeUrl(st.writeTarget);
    var back = M.writeView.querySelector('[data-ca-rv-back]');
    if (back) back.focus();
  }

  function open(opts) {
    if (!M) return false;
    opts = opts || {};
    var productId = String(opts.productId || 'all');
    M.state = {
      productId: productId,
      writeTarget: opts.writeTarget || (productId === 'all' ? 'store' : productId),
      offset: 0,
      summaryShown: false
    };
    M.title.textContent = opts.title || cfg.storeTitle || '';
    M.filter.value = '';
    M.sort.value = '3';
    M.rating.value = '';
    M.summary.hidden = true;
    M.bars.hidden = true;
    M.tabcount.textContent = '';
    if (!dlg.open) dlg.showModal();
    if (opts.view === 'write') {
      showWrite();
      // Load the list behind the form, so "Back to reviews" is instant.
      load(true);
    } else {
      showList();
      load(true);
    }
    return true;
  }

  if (M) {
    var debounce;
    M.filter.addEventListener('input', function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () { load(true); }, 320);
    });
    M.sort.addEventListener('change', function () { load(true); });
    M.rating.addEventListener('change', function () { syncBars(); load(true); });
    M.more.addEventListener('click', function () { load(false); });
    M.bars.addEventListener('click', function (e) {
      var b = e.target.closest('[data-ca-rv-bar]');
      if (!b) return;
      var v = b.getAttribute('data-ca-rv-bar');
      M.rating.value = M.rating.value === v ? '' : v;
      syncBars();
      load(true);
    });
    M.frame.addEventListener('load', function () { M.frameLoading.hidden = true; });

    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-ca-rv-close]')) { dlg.close(); return; }
      if (e.target.closest('[data-ca-rv-write]')) { showWrite(); return; }
      if (e.target.closest('[data-ca-rv-back]')) { showList(); return; }
      // A click on the backdrop lands on the <dialog> itself.
      if (e.target === dlg) dlg.close();
    });
    dlg.addEventListener('close', function () {
      M.token++;
      M.frame.removeAttribute('src');
    });

    // Klaviyo's hosted form posts its state to the parent, as it does for
    // Klaviyo's own widget. "close" means the customer dismissed it.
    window.addEventListener('message', function (e) {
      if (!dlg.open || M.writeView.hidden) return;
      if (!/(^|\.)klaviyo\.com$/.test((function () { try { return new URL(e.origin).hostname; } catch (x) { return ''; } })())) return;
      if (e.data === 'close') showList();
      if (e.data === 'loaded' || e.data === 'loaded-logo') M.frameLoading.hidden = true;
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ca-rv-open]');
    if (!t || !M) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    open({
      productId: t.getAttribute('data-ca-rv-product') || 'all',
      title: t.getAttribute('data-ca-rv-title') || '',
      view: t.getAttribute('data-ca-rv-view') || 'list'
    });
  });

  /* ---------- homepage: live rating + random review cards ---------- */

  function hydrateSummary(root, sum) {
    if (!sum || !sum.review_count) return;
    root.querySelectorAll('[data-ca-rv-out="avg"]').forEach(function (el) { el.textContent = fmtAvg(sum.star_rating); });
    root.querySelectorAll('[data-ca-rv-out="stars"]').forEach(function (el) {
      el.style.setProperty('--r', rVal(sum.star_rating));
      el.setAttribute('aria-label', 'Rated ' + fmtAvg(sum.star_rating) + ' out of 5 from ' + fmtCount(sum.review_count) + ' reviews');
    });
    root.querySelectorAll('[data-ca-rv-out="count"]').forEach(function (el) {
      var tpl = el.getAttribute('data-template') || '[count]';
      el.textContent = tpl.replace(/\[count\]/g, fmtCount(sum.review_count));
    });
  }

  function homeCard(r, maxChars) {
    var verified = r.verified ? '<span class="ca-press__review-verified"><svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>Verified buyer</span>' : '';
    return '<li class="ca-press__review">' +
      '<span class="ca-press__review-glyph" aria-hidden="true">“</span>' +
      '<div class="ca-press__review-stars">' + stars(r.rating) + '</div>' +
      (r.title ? '<p class="ca-press__review-title">' + esc(r.title) + '</p>' : '') +
      '<p class="ca-press__review-text">' + esc(trimWords(r.content, maxChars)) + '</p>' +
      '<p class="ca-press__review-by"><span class="ca-press__review-name">' + esc(r.author || 'Cocoa Asante customer') + '</span>' + verified + '</p>' +
      (r.product && r.product.name ? '<p class="ca-press__review-product">' + esc(r.product.name) + '</p>' : '') +
      '</li>';
  }

  function initHome(root) {
    var productId = root.getAttribute('data-ca-rv-product') || 'all';
    var want = parseInt(root.getAttribute('data-count'), 10) || 0;
    var minRating = parseFloat(root.getAttribute('data-min-rating')) || 4;
    var maxChars = parseInt(root.getAttribute('data-max-chars'), 10) || 220;
    var list = root.querySelector('[data-ca-rv-cards]');

    function pick(d) {
      return d.reviews.filter(function (r) {
        var len = (r.content || '').trim().length;
        return r.rating >= minRating && len >= 30 && len <= maxChars * 2.2;
      });
    }

    // Random window into the full review set, so every visit shows different
    // reviews; Klaviyo caps how deep an offset can go, so fall back to page 1.
    var offset = Math.floor(Math.random() * (MAX_OFFSET + 1));
    var q = { productId: productId, limit: MAX_LIMIT, sort: 0, rating: minRating >= 5 ? 5 : '' };
    getPage(Object.assign({ offset: offset }, q))
      .then(function (d) { return pick(d).length >= want ? d : getPage(Object.assign({ offset: 0 }, q)); })
      .catch(function () { return getPage(Object.assign({ offset: 0 }, q)); })
      .then(function (d) {
        hydrateSummary(root, d.summary);
        if (!list || !want) return;
        var chosen = shuffle(pick(d)).slice(0, want);
        if (!chosen.length) { list.hidden = true; return; }
        list.innerHTML = chosen.map(function (r) { return homeCard(r, maxChars); }).join('');
        list.removeAttribute('aria-busy');
      })
      .catch(function () {
        // Keep the server-rendered rating; drop the card placeholders.
        if (list) list.hidden = true;
      });
  }

  /* ---------- star badges with no server-side rating ---------- */

  function initBadges(badges) {
    var ids = badges.map(function (b) { return b.getAttribute('data-ca-rv-product'); });
    getSummaries(ids).then(function (map) {
      badges.forEach(function (b) {
        var p = map[b.getAttribute('data-ca-rv-product')];
        var n = (p && p.review_count) || 0;
        var avgEl = b.querySelector('[data-ca-rv-out="avg"]');
        var countEl = b.querySelector('[data-ca-rv-out="count"]');
        var starsEl = b.querySelector('[data-ca-rv-out="stars"]');
        if (n > 0) {
          if (starsEl) starsEl.style.setProperty('--r', rVal(p.star_rating));
          if (avgEl) { avgEl.textContent = fmtAvg(p.star_rating); avgEl.hidden = false; }
          if (countEl) countEl.textContent = fmtCount(n) + ' ' + plural(n, 'review', 'reviews');
          b.setAttribute('aria-label', 'Rated ' + fmtAvg(p.star_rating) + ' out of 5 from ' + fmtCount(n) + ' reviews');
        } else {
          if (starsEl) starsEl.style.setProperty('--r', '0');
          if (countEl) countEl.textContent = b.getAttribute('data-empty-label') || 'Be the first to review';
          b.setAttribute('aria-label', countEl ? countEl.textContent : 'Reviews');
        }
        b.removeAttribute('data-loading');
      });
    }).catch(function () {
      badges.forEach(function (b) { b.removeAttribute('data-loading'); });
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-ca-rv-home]:not([data-ca-rv-ready])').forEach(function (el) {
      el.setAttribute('data-ca-rv-ready', '');
      initHome(el);
    });
    var badges = Array.prototype.slice.call((scope || document).querySelectorAll('[data-ca-rv-badge]:not([data-ca-rv-ready])'));
    badges.forEach(function (b) { b.setAttribute('data-ca-rv-ready', ''); });
    if (badges.length) initBadges(badges);
  }

  boot();
  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.CAReviews = { open: open };
})();
